import React, { useState, useEffect } from 'react';
import '../styles/AdminDashboard.css';

const AdminDashboard = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/applications/all`);
      
      if (!response.ok) {
        throw new Error('Fehler beim Laden der Bewerbungen');
      }
      
      const data = await response.json();
      setApplications(data.applications || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatQualificationAnswer = (value) => {
    if (!value) return '-';
    
    const translations = {
      // Current stage
      'starting_from_zero': 'Starting from zero',
      'posting_but_stuck': 'Posting but stuck',
      'coach_or_expert': 'Coach/Expert',
      'audience_not_monetizing': 'Has audience, not monetizing',
      
      // Biggest challenge
      'content_not_spreading': 'Content not spreading',
      'inconsistent_no_system': 'Inconsistent, no system',
      'no_reach_engagement': 'No reach/engagement',
      'no_monetization': 'No monetization',
      
      // Readiness
      'ready_immediately': '✅ Ready immediately',
      'gathering_information': 'Gathering information',
      'few_months': 'In a few months',
      
      // Investment ready
      'serious_investment': '✅ Ready for serious investment',
      'limited_budget': '⚠️ Budget under €500',
      'not_sure': '❌ Not sure',
      
      // Expectations
      'ready_to_work': '✅ Ready to work',
      'do_it_for_me': '❌ Wants it done for them',
      'quick_results': '⚠️ Quick results, no effort'
    };
    
    return translations[value] || value;
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Bewerbungen - Admin Dashboard</h1>
        <button onClick={fetchApplications} className="refresh-button">
          Aktualisieren
        </button>
      </div>

      {loading && <p className="loading">Lade Bewerbungen...</p>}
      
      {error && (
        <div className="error-message">
          <p>Fehler: {error}</p>
        </div>
      )}

      {!loading && !error && (
        <div className="applications-container">
          <div className="stats">
            <p>Gesamt: <strong>{applications.length}</strong> Bewerbungen</p>
          </div>

          {applications.length === 0 ? (
            <p className="no-applications">Noch keine Bewerbungen vorhanden.</p>
          ) : (
            <div className="applications-list">
              {applications.map((app) => (
                <div key={app.id} className="application-card">
                  <div className="card-header">
                    <h3>{app.name}</h3>
                    <span className="timestamp">{formatDate(app.created_at)}</span>
                  </div>
                  
                  <div className="card-body">
                    <div className="info-row">
                      <span className="label">E-Mail:</span>
                      <a href={`mailto:${app.email}`} className="email-link">
                        {app.email}
                      </a>
                    </div>
                    
                    {app.instagram && (
                      <div className="info-row">
                        <span className="label">Instagram:</span>
                        <span className="value">{app.instagram}</span>
                      </div>
                    )}
                    
                    <div className="qualification-section">
                      <h4 className="qualification-title">Qualifizierung</h4>
                      
                      <div className="qual-item">
                        <span className="qual-label">Aktueller Stand:</span>
                        <span className="qual-value">{formatQualificationAnswer(app.current_stage)}</span>
                      </div>
                      
                      <div className="qual-item">
                        <span className="qual-label">Größte Herausforderung:</span>
                        <span className="qual-value">{formatQualificationAnswer(app.biggest_challenge)}</span>
                      </div>
                      
                      <div className="qual-item">
                        <span className="qual-label">Bereitschaft:</span>
                        <span className="qual-value qual-highlight">{formatQualificationAnswer(app.readiness)}</span>
                      </div>
                      
                      <div className="qual-item">
                        <span className="qual-label">Investment:</span>
                        <span className="qual-value qual-highlight">{formatQualificationAnswer(app.investment_ready)}</span>
                      </div>
                      
                      <div className="qual-item">
                        <span className="qual-label">Erwartungen:</span>
                        <span className="qual-value">{formatQualificationAnswer(app.expectations)}</span>
                      </div>
                    </div>
                    
                    <div className="message-section">
                      <span className="label">Was würde sich ändern?</span>
                      <p className="message-text">{app.life_change}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
